<template>
  <div v-if="canSubmit">
    <!-- Floating entry point. Hidden while picking an element and while the
         browser captures a frame, so it never lands in the screenshot.
         Sits below dropdowns/popovers (z-sidebar), else it covers their
         bottom-right buttons, e.g. the date picker's Apply. -->
    <div
      v-if="floating && !open && !hidden && !pickerActive && !dismissed"
      class="group fixed bottom-0 right-0 z-sidebar flex items-center gap-1 p-4"
    >
      <button
        type="button"
        data-feedback-ui
        data-test="feedback-launcher"
        class="inline-flex items-center gap-2 rounded-full border border-theme-border bg-theme-surface-raised px-4 py-2.5 text-sm font-medium text-theme-text shadow-theme-elevated transition hover:bg-theme-bg-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus"
        :aria-label="t('feedback.menu')"
        @click="openPanel"
      >
        <icon :path="mdiMessageAlertOutline" size="18" />
        <span class="hidden sm:inline">{{ t('feedback.menu') }}</span>
      </button>
      <button
        type="button"
        data-feedback-ui
        data-test="feedback-launcher-dismiss"
        class="inline-flex h-6 w-6 items-center justify-center rounded-full border border-theme-border bg-theme-surface-raised text-theme-text-subtle shadow-theme-elevated opacity-0 transition-opacity hover:bg-theme-bg-subtle focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-theme-focus group-hover:opacity-100"
        :aria-label="t('feedback.hide')"
        :title="t('feedback.hide')"
        @click="dismissLauncher"
      >
        <icon :path="mdiClose" size="12" w="w-3" h="h-3" />
      </button>
    </div>

    <feedback-panel v-if="open && !hidden && !pickerActive && !editingScreenshotId" @submit="onSubmit" />

    <!-- Full-screen annotation. Replaces the panel rather than sitting on top
         of it: the whole point is to see the screenshot at size. -->
    <screenshot-editor-dialog
      v-if="editingScreenshotId"
      :shot-id="editingScreenshotId"
      @done="closeEditor"
      @remove="discardEditedScreenshot"
    />

    <!-- Region selector: a dimmed sheet you draw the capture box on. The
         composable listens in the capture phase, so clicks that land here
         never reach the page underneath. -->
    <template v-if="regionActive">
      <div
        data-feedback-ui
        data-test="feedback-region-overlay"
        class="fixed inset-0 z-dialog cursor-crosshair bg-theme-bg/30"
      >
        <div
          v-if="regionStyle"
          data-test="feedback-region-box"
          class="absolute border-2 border-theme-focus bg-theme-focus/10"
          :style="regionStyle"
        ></div>
      </div>
      <div
        data-feedback-ui
        role="status"
        class="pointer-events-none fixed left-1/2 top-4 z-toast -translate-x-1/2 rounded-full border border-theme-border bg-theme-surface-raised px-4 py-2 text-xs text-theme-text shadow-theme-elevated"
      >
        {{ regionAnchor ? t('feedback.region_hint_second') : t('feedback.region_hint_first') }}
      </div>
    </template>

    <!-- Element picker chrome: an outline that follows the candidate, plus a
         banner that says how to finish. Both are pointer-transparent. -->
    <template v-if="pickerActive">
      <div
        v-if="highlightStyle"
        data-feedback-ui
        class="pointer-events-none fixed z-overlay rounded-theme-sm border-2 border-theme-focus bg-theme-focus/10"
        :style="highlightStyle"
      ></div>
      <div
        data-feedback-ui
        role="status"
        class="pointer-events-none fixed left-1/2 top-4 z-toast -translate-x-1/2 rounded-full border border-theme-border bg-theme-surface-raised px-4 py-2 text-xs text-theme-text shadow-theme-elevated"
      >
        {{ t('feedback.picker_hint') }}
      </div>
    </template>
  </div>
</template>

<script setup>
  /**
   * Mounts the whole reporting flow: the floating button, the panel, and the
   * element-picker overlay. Rendered from `App.vue` for every session, but it
   * renders nothing until the backend confirms this account is on the submit
   * allowlist.
   *
   * Other entry points (the profile menu, the contextual error prompt) reach
   * it through the `feedback:open` window event, the same way `UpgradeModal`
   * is tripped — so a non-Vue caller does not need a component reference.
   */
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  import { mdiClose, mdiMessageAlertOutline } from '@mdi/js';

  import Icon from '@/components/Icon';
  import { FEEDBACK_OPEN_EVENT } from '@/composables/useFeedback';
  import { useElementPicker } from '@/features/feedback/composables/useElementPicker';
  import { bindFeedbackFocus } from '@/features/feedback/composables/useFeedbackFocus';
  import {
    resetFeedbackAccess,
    useFeedbackAccess,
  } from '@/features/feedback/composables/useFeedbackAccess';
  import { useRegionSelector } from '@/features/feedback/composables/useRegionSelector';
  import {
    bindSelectionTracking,
    useFeedbackReporter,
  } from '@/features/feedback/composables/useFeedbackReporter';
  import { useAuthStore } from '@/stores/useAuthStore';
  import { afterCriticalContent } from '@/utils/deferNonCritical';
  import FeedbackPanel from './FeedbackPanel.vue';
  import ScreenshotEditorDialog from './ScreenshotEditorDialog.vue';

  const props = defineProps({
    /** Show the floating button. Off where the host places its own entry point (admin sidebar). */
    floating: { type: Boolean, default: true },
  });
  const floating = computed(() => props.floating);

  const { t } = useI18n();
  const route = useRoute();
  const authStore = useAuthStore();

  const { canSubmit, refresh } = useFeedbackAccess();
  const { active: pickerActive, hovered, cancel: cancelPicker } = useElementPicker();
  const {
    active: regionActive,
    anchor: regionAnchor,
    pointer: regionPointer,
    cancel: cancelRegion,
  } = useRegionSelector();
  const {
    open,
    hidden,
    dismissed,
    openPanel,
    submit,
    editingScreenshotId,
    closeEditor,
    removeScreenshot,
    dismissLauncher,
  } = useFeedbackReporter();

  function discardEditedScreenshot() {
    removeScreenshot(editingScreenshotId.value);
  }

  const highlightRect = ref(null);

  const highlightStyle = computed(() => {
    const rect = highlightRect.value;
    if (!rect) return null;
    return {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    };
  });

  watch(hovered, (element) => {
    highlightRect.value = element ? element.getBoundingClientRect() : null;
  });

  // The live rubber band: fixed corner at the first click, the other corner
  // following the pointer until the second one lands.
  const regionStyle = computed(() => {
    const from = regionAnchor.value;
    const to = regionPointer.value;
    if (!from || !to) return null;
    return {
      left: `${Math.min(from.x, to.x)}px`,
      top: `${Math.min(from.y, to.y)}px`,
      width: `${Math.abs(from.x - to.x)}px`,
      height: `${Math.abs(from.y - to.y)}px`,
    };
  });

  function onSubmit() {
    submit(route);
  }

  function onOpenRequest() {
    if (canSubmit.value) openPanel();
  }

  let cancelRefresh = () => {};

  onMounted(() => {
    bindSelectionTracking();
    // A reviewer arriving from the admin portal's "open the page" link lands
    // with `?feedback_focus=…` on the URL; this is what makes it land on the
    // element the report is actually about.
    bindFeedbackFocus();
    window.addEventListener(FEEDBACK_OPEN_EVENT, onOpenRequest);
    // The launcher button is not needed for first paint: probe the capability
    // once the page's main content has settled.
    if (authStore.isAuthenticated) cancelRefresh = afterCriticalContent(refresh);
  });

  onBeforeUnmount(() => {
    cancelRefresh();
    window.removeEventListener(FEEDBACK_OPEN_EVENT, onOpenRequest);
    if (pickerActive.value) cancelPicker();
    if (regionActive.value) cancelRegion();
  });

  // A session can start unauthenticated (login page) — ask again once the user
  // is in, rather than leaving the button permanently off. On the way out the
  // verdict is dropped, so the next account never inherits this one's access.
  watch(
    () => authStore.isAuthenticated,
    (isAuthenticated) => {
      cancelRefresh();
      if (isAuthenticated) cancelRefresh = afterCriticalContent(refresh);
      else resetFeedbackAccess();
    }
  );
</script>
